import type { ThemeOptions } from '@mui/material/styles';
// types only: lets `components` below name the date pickers
import type {} from '@mui/x-date-pickers/themeAugmentation';

// Rules for touch screens, merged into both themes by theme.tsx. They key on
// the input device, not on the width: a tablet in landscape is wider than
// 900 px and still has fingers. Touch-screen laptops get the larger targets
// too (accepted in the design).
export const COARSE = '@media (pointer: coarse)';
export const MIN_TARGET_PX = 44;

const minTarget = { minWidth: MIN_TARGET_PX, minHeight: MIN_TARGET_PX };

/**
 * Below 600 px every dialog is a screen (the rule at the bottom of this
 * file). A small confirmation or message, where a whole screen is wrong, says
 * so with `<Dialog className={KEEP_DIALOG_SIZE}>` and keeps the size it asked
 * for. MUI X's phone date picker is a Dialog around a calendar: the theme
 * gives every date picker this class (`pickerDialog` below), so a page does
 * not have to.
 */
export const KEEP_DIALOG_SIZE = 'keep-dialog-size';

// What the phone variant of a date picker draws as its Dialog keeps its size.
// slotProps merge with a page's own (Portfolio sets textField).
const pickerDialog = {
    defaultProps: { slotProps: { dialog: { className: KEEP_DIALOG_SIZE } } },
};

/**
 * Spread into the `sx` of a control that lives in a fixed-height row and must
 * keep its size on a touch screen until its page is redesigned. It has to be
 * inside the media query: the theme's rule is a media block, which is emitted
 * after the plain declarations of the same class, so a plain `minHeight: 0`
 * would lose to it.
 */
export const NO_MIN_TARGET = {
    [COARSE]: { minWidth: 0, minHeight: 0 },
} as const;

/**
 * Spread into the `sx` of a ToggleButtonGroup that sits in a fixed-height row
 * and whose buttons opt out with NO_MIN_TARGET. The theme gives every group
 * `height: auto` on a touch screen (see MuiToggleButtonGroup below), which
 * would also override this group's own `height`; the pin has to be inside the
 * media query, after the theme's, to win.
 */
export const keepGroupHeight = (px: number) =>
    ({ height: px, [COARSE]: { height: px } }) as const;

export const touchComponents: NonNullable<ThemeOptions['components']> = {
    MuiTooltip: {
        // No touch timing here: MUI's own (a long press opens it, 1.5 s after
        // the finger lifts it closes) stays. A zero delay opens a tooltip on
        // the touch start, which also pops one up for 4 s when a scroll just
        // begins on a tooltip-wrapped element, and on iOS leaves a menu
        // button's tooltip above its menu. A tooltip that a tap should open
        // (an info trigger) is a TapTooltip (src/components/touch).
        styleOverrides: {
            // The bubble the touch mockup (K4) draws: 13 px text, a darker
            // fill and a shadow, with its arrow. Touch screens only; a mouse
            // keeps MUI's.
            tooltip: {
                [COARSE]: {
                    fontSize: 13,
                    lineHeight: '18px',
                    padding: '8px 10px',
                    borderRadius: 6,
                    backgroundColor: 'rgba(50, 50, 50, 0.96)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                },
            },
            arrow: {
                [COARSE]: { color: 'rgba(50, 50, 50, 0.96)' },
            },
        },
    },
    MuiButton: { styleOverrides: { root: { [COARSE]: minTarget } } },
    MuiIconButton: { styleOverrides: { root: { [COARSE]: minTarget } } },
    MuiTab: { styleOverrides: { root: { [COARSE]: minTarget } } },
    MuiToggleButton: { styleOverrides: { root: { [COARSE]: minTarget } } },
    // The group has to grow with its 44 px buttons: toolbars give it a fixed
    // 24-26 px height, and the buttons would spill out of it (a tablet at
    // desktop width has fingers too). Declared here, in the media query, it
    // beats a plain `height` in any page's `sx` (the media block is printed
    // after the plain declarations of the same class). A group whose buttons
    // opt out with NO_MIN_TARGET is as high as those buttons then.
    MuiToggleButtonGroup: {
        styleOverrides: { root: { [COARSE]: { height: 'auto' } } },
    },
    // Only a chip that does something: a label chip stays a label.
    MuiChip: { styleOverrides: { clickable: { [COARSE]: minTarget } } },
    MuiDatePicker: pickerDialog,
    MuiMobileDatePicker: pickerDialog,
    MuiDialog: {
        styleOverrides: {
            // Below 600 px a dialog is a screen, whatever width, height or
            // margin it asked for. A descendant selector beats the paper's
            // own class, including `maxWidth` and `PaperProps`. Except the
            // ones that keep their size (KEEP_DIALOG_SIZE), among them the
            // date pickers' calendars (no :has() here: a browser without it
            // would drop the whole rule).
            root: ({ theme }) => ({
                [theme.breakpoints.down('sm')]: {
                    [`&:not(.${KEEP_DIALOG_SIZE}) .MuiDialog-paper`]: {
                        boxSizing: 'border-box',
                        margin: 0,
                        width: '100%',
                        maxWidth: '100%',
                        height: '100%',
                        maxHeight: 'none',
                        borderRadius: 0,
                        // viewport-fit=cover: keep clear of the notch and the
                        // home indicator (env() is 0 everywhere else).
                        paddingTop: 'env(safe-area-inset-top, 0px)',
                        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                        paddingLeft: 'env(safe-area-inset-left, 0px)',
                        paddingRight: 'env(safe-area-inset-right, 0px)',
                    },
                },
            }),
        },
    },
};
